import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UsersApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useUserBulkDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (userIds: number[]) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(UsersApi, accessToken);
      await Promise.all(
        userIds.map((id) => service.apiUserIdDelete({ id }))
      );
    },
    onSuccess: async (_data, userIds) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.UsersScrollGet],
      });
      const count = userIds.length;
      toast({
        title: "Users deleted",
        description: `${count} user${count === 1 ? "" : "s"} deleted successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "Failed to delete users"
      );
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
